import React, { useEffect, useState } from 'react';
import './Health-Score-Config.scss';
import { Button, IconButton, MenuItem, Select, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import DeleteIcon from '@material-ui/icons/Delete';
import { useTranslation } from 'react-i18next';
import HealthScoreService from '../../../services/health-score.service';
import {
    AvailableCustomFields,
    ConfigDraft,
    CUSTOM_FIELDS_PATH,
    customFieldsMaxPoints,
    customFieldsTotal,
    editableParameters,
    errorsFor,
    getAt,
    HealthScoreConfig,
    toDraft,
    toOverrides,
    ValidationError,
    validationErrors,
} from '../../../services/health-score-config';
import Loading from '../custom-components/loading/loading';
import NotificationSnackbar from '../custom-components/notificationSnackbar/NotificationSnackbar';

interface HealthScoreConfigProps {
    clientId: string;
}

/** Sections de l'écran : première clé des chemins du fichier des défauts. */
const SECTIONS = ['completion', 'ereputation', 'animation', 'pending'];

/** Statut HTTP d'une erreur axios, undefined pour une erreur réseau. */
function httpStatus(error: any): number | undefined {
    return error?.response?.status;
}

const HealthScoreConfigPanel: React.FC<HealthScoreConfigProps> = ({ clientId = '' }) => {
    const { t } = useTranslation();
    const [config, setConfig] = useState<HealthScoreConfig | null>(null);
    const [draft, setDraft] = useState<ConfigDraft>({ values: {}, customFields: null, others: {} });
    const [availableFields, setAvailableFields] = useState<AvailableCustomFields[]>([]);
    const [errors, setErrors] = useState<ValidationError[]>([]);
    const [conflict, setConflict] = useState(false);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    const notify = (message: string, severity: 'success' | 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    /** Affiche une configuration lue ou renvoyée par un enregistrement. */
    const show = (loaded: HealthScoreConfig) => {
        setConfig(loaded);
        setDraft(toDraft(loaded));
        setErrors([]);
        setConflict(false);
    };

    const load = async () => {
        setLoading(true);
        // sans la liste des champs custom, le reste de la configuration reste modifiable
        const fields = HealthScoreService.getCustomFields(clientId)
            .then(response => response.data ?? [])
            .catch(() => {
                notify(t('parameters.healthScore.messages.customFieldsFailed'), 'error');
                return [];
            });
        try {
            const response = await HealthScoreService.getConfig(clientId);
            show(response.data);
        } catch (error) {
            setConfig(null);
            notify(t('parameters.healthScore.messages.loadFailed'), 'error');
        }
        setAvailableFields(await fields);
        setLoading(false);
    };

    useEffect(() => {
        if (clientId !== '') {
            load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    /** Surcharge prête à l'envoi, ou null (et l'erreur affichée) si un champ n'est pas un nombre. */
    const overridesToSend = () => {
        const { overrides, invalid } = toOverrides(draft);
        if (invalid.length > 0) {
            setErrors(invalid.map(field => ({ field, messages: [t('parameters.healthScore.messages.notANumber')] })));
            return null;
        }
        return overrides;
    };

    /** Erreur d'appel : erreurs par champ pour un 400, conflit pour un 409, message sinon. */
    const handleError = (error: any, fallbackMessage: string) => {
        const status = httpStatus(error);
        if (status === 400) {
            const problemErrors = validationErrors(error.response.data);
            setErrors(problemErrors.length > 0
                ? problemErrors
                : [{ field: '', messages: [t('parameters.healthScore.messages.invalid')] }]);
        } else if (status === 409) {
            setConflict(true);
        } else {
            notify(fallbackMessage, 'error');
        }
    };

    const handleValidate = async () => {
        const overrides = overridesToSend();
        if (overrides === null) {
            return;
        }
        setBusy(true);
        try {
            await HealthScoreService.validateConfig(clientId, overrides);
            setErrors([]);
            notify(t('parameters.healthScore.messages.valid'), 'success');
        } catch (error) {
            handleError(error, t('parameters.healthScore.messages.validateFailed'));
        }
        setBusy(false);
    };

    const handleSave = async () => {
        const overrides = overridesToSend();
        if (overrides === null || config === null) {
            return;
        }
        setBusy(true);
        try {
            const response = await HealthScoreService.updateConfig(clientId, overrides, config.version);
            show(response.data);
            notify(t('parameters.healthScore.messages.saved'), 'success');
        } catch (error) {
            handleError(error, t('parameters.healthScore.messages.saveFailed'));
        }
        setBusy(false);
    };

    const handleResetAll = async () => {
        if (!window.confirm(t('parameters.healthScore.messages.confirmReset'))) {
            return;
        }
        setBusy(true);
        try {
            const response = await HealthScoreService.resetConfig(clientId);
            show(response.data);
            notify(t('parameters.healthScore.messages.resetDone'), 'success');
        } catch (error) {
            handleError(error, t('parameters.healthScore.messages.resetFailed'));
        }
        setBusy(false);
    };

    const setValue = (path: string, text: string | undefined) => {
        setDraft(previous => {
            const values = { ...previous.values };
            if (text === undefined) {
                delete values[path];
            } else {
                values[path] = text;
            }
            return { ...previous, values };
        });
    };

    const setCustomFields = (customFields: ConfigDraft['customFields']) => {
        setDraft(previous => ({ ...previous, customFields }));
    };

    if (loading) {
        return <Loading />;
    }
    if (config === null) {
        return (
            <div className="health-score-config-page">
                <Button variant="outlined" onClick={load}>{t('parameters.healthScore.actions.reload')}</Button>
                <NotificationSnackbar open={snackbarOpen} message={snackbarMessage}
                    onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} />
            </div>
        );
    }

    const paths = editableParameters(config.defaults);
    const label = (path: string) => t(`parameters.healthScore.params.${path}.label`, { defaultValue: path });
    const help = (path: string) => t(`parameters.healthScore.params.${path}.help`, { defaultValue: '' });
    /** Libellé du paramètre visé par une erreur du module, sinon sa clé telle quelle. */
    const fieldLabel = (field: string) => {
        const path = paths.find(candidate => candidate === field);
        return path !== undefined ? label(path) : field;
    };

    const renderParameter = (path: string) => {
        const overridden = path in draft.values;
        const fieldErrors = errorsFor(errors, path);
        return (
            <tr key={path} className={overridden ? 'overridden' : ''}>
                <td>
                    <div className="parameter-label">{label(path)}</div>
                    <div className="parameter-help">{help(path)}</div>
                </td>
                <td className="parameter-default">{String(getAt(config.defaults, path))}</td>
                <td>
                    {overridden ? (
                        <TextField
                            value={draft.values[path]}
                            onChange={(event) => setValue(path, event.target.value)}
                            error={fieldErrors.length > 0}
                            helperText={fieldErrors.join(' ')}
                            inputProps={{ inputMode: 'decimal', 'aria-label': label(path) }}
                        />
                    ) : (
                        <span className="parameter-not-overridden">{t('parameters.healthScore.default')}</span>
                    )}
                </td>
                <td>
                    {overridden ? (
                        <Button size="small" onClick={() => setValue(path, undefined)}>
                            {t('parameters.healthScore.actions.backToDefault')}
                        </Button>
                    ) : (
                        <Button size="small" onClick={() => setValue(path, String(getAt(config.defaults, path)))}>
                            {t('parameters.healthScore.actions.override')}
                        </Button>
                    )}
                </td>
            </tr>
        );
    };

    const renderCustomFields = () => {
        const defaults = (getAt(config.defaults, CUSTOM_FIELDS_PATH) as { metadataType: string; attributeCode: string; points: number }[] | undefined) ?? [];
        const overridden = draft.customFields !== null;
        const fields = draft.customFields ?? defaults.map(field => ({ ...field, points: String(field.points) }));
        const total = customFieldsTotal(fields);
        const max = customFieldsMaxPoints(config);
        const fieldErrors = errorsFor(errors, CUSTOM_FIELDS_PATH);
        const updateField = (index: number, change: Partial<typeof fields[number]>) => {
            setCustomFields(fields.map((field, i) => (i === index ? { ...field, ...change } : field)));
        };
        const codesOf = (metadataType: string) =>
            availableFields.find(available => available.metadataType === metadataType)?.attributeCodes ?? [];

        return (
            <div className="health-score-section custom-fields">
                <h3>{t('parameters.healthScore.customFields.title')}</h3>
                <div className="parameter-help">{t('parameters.healthScore.customFields.help', { max })}</div>
                {fields.length === 0 && <p>{t('parameters.healthScore.customFields.empty')}</p>}
                {fields.map((field, index) => (
                    <div key={index} className="custom-field-row">
                        <Select
                            value={field.metadataType}
                            disabled={!overridden}
                            displayEmpty
                            onChange={(event) => updateField(index, { metadataType: event.target.value as string, attributeCode: '' })}
                        >
                            <MenuItem value="" disabled>{t('parameters.healthScore.customFields.type')}</MenuItem>
                            {/* un type configuré qui n'existe plus chez le client reste affiché : le module le refusera */}
                            {Array.from(new Set([field.metadataType, ...availableFields.map(available => available.metadataType)]))
                                .filter(type => type !== '')
                                .map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                        </Select>
                        <Select
                            value={field.attributeCode}
                            disabled={!overridden || field.metadataType === ''}
                            displayEmpty
                            onChange={(event) => updateField(index, { attributeCode: event.target.value as string })}
                        >
                            <MenuItem value="" disabled>{t('parameters.healthScore.customFields.code')}</MenuItem>
                            {Array.from(new Set([field.attributeCode, ...codesOf(field.metadataType)]))
                                .filter(code => code !== '')
                                .map(code => <MenuItem key={code} value={code}>{code}</MenuItem>)}
                        </Select>
                        <TextField
                            label={t('parameters.healthScore.customFields.points')}
                            value={field.points}
                            disabled={!overridden}
                            onChange={(event) => updateField(index, { points: event.target.value })}
                            inputProps={{ inputMode: 'decimal' }}
                        />
                        {overridden && (
                            <IconButton onClick={() => setCustomFields(fields.filter((_, i) => i !== index))}>
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </div>
                ))}
                <div className={total > max ? 'custom-fields-total over' : 'custom-fields-total'}>
                    {t('parameters.healthScore.customFields.total', { total, max })}
                </div>
                {fieldErrors.map(message => <Alert key={message} severity="error">{message}</Alert>)}
                {overridden ? (
                    <>
                        <Button size="small" onClick={() => setCustomFields([...fields, { metadataType: '', attributeCode: '', points: '1' }])}>
                            {t('parameters.healthScore.customFields.add')}
                        </Button>
                        <Button size="small" onClick={() => setCustomFields(null)}>
                            {t('parameters.healthScore.actions.backToDefault')}
                        </Button>
                    </>
                ) : (
                    <Button size="small" onClick={() => setCustomFields(fields)}>
                        {t('parameters.healthScore.actions.override')}
                    </Button>
                )}
            </div>
        );
    };

    return (
        <div className="health-score-config-page">
            <p className="health-score-intro">{t('parameters.healthScore.intro')}</p>
            <p className="health-score-version">
                {config.updatedAt
                    ? t('parameters.healthScore.version', { version: config.version, updatedAt: new Date(config.updatedAt).toLocaleString(), updatedBy: config.updatedBy ?? '' })
                    : t('parameters.healthScore.noOverride')}
            </p>
            {conflict && (
                <Alert severity="warning" action={
                    <Button color="inherit" size="small" onClick={load}>{t('parameters.healthScore.actions.reload')}</Button>
                }>
                    {t('parameters.healthScore.messages.conflict')}
                </Alert>
            )}
            {errors.length > 0 && (
                <Alert severity="error" className="health-score-errors">
                    <div>{t('parameters.healthScore.messages.invalid')}</div>
                    <ul>
                        {errors.map(error => error.messages.map(message => (
                            <li key={`${error.field}-${message}`}>
                                {error.field !== '' && <strong>{fieldLabel(error.field)} : </strong>}{message}
                            </li>
                        )))}
                    </ul>
                </Alert>
            )}
            {SECTIONS.map(section => {
                const sectionPaths = paths.filter(path => path.split('.')[0] === section);
                if (sectionPaths.length === 0) {
                    return null;
                }
                return (
                    <div key={section} className="health-score-section">
                        <h3>{t(`parameters.healthScore.sections.${section}`, { defaultValue: section })}</h3>
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('parameters.healthScore.columns.parameter')}</th>
                                    <th>{t('parameters.healthScore.columns.default')}</th>
                                    <th>{t('parameters.healthScore.columns.override')}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>{sectionPaths.map(renderParameter)}</tbody>
                        </table>
                        {section === 'completion' && renderCustomFields()}
                    </div>
                );
            })}
            <div className="health-score-actions">
                <Button variant="outlined" disabled={busy} onClick={handleValidate}>
                    {t('parameters.healthScore.actions.validate')}
                </Button>
                <Button variant="contained" disabled={busy || conflict} onClick={handleSave}
                    style={{ backgroundColor: '#15afad', color: 'white' }}>
                    {t('parameters.healthScore.actions.save')}
                </Button>
                <Button variant="outlined" color="secondary" disabled={busy} onClick={handleResetAll}>
                    {t('parameters.healthScore.actions.resetAll')}
                </Button>
            </div>
            <NotificationSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                onClose={() => setSnackbarOpen(false)}
                severity={snackbarSeverity}
            />
        </div>
    );
};

export default HealthScoreConfigPanel;
